import _ from "lodash";
import React, { useState } from "react";
import { formatMass } from "@signolteam/shared/units-of-measure";
import { Co2Equivalences } from "@signolteam/shared/co2-equivalences";
import {
  HorizontalLayout,
  HorizontalResponsiveLayout,
  VerticalLayout,
  CardContainer,
  SavingsCard,
  SavingsEquivalenceFlippableCard,
} from "@src/components/cards";
import { DropDown, ImageWrapper, Tooltip } from "@src/components/atoms";
import { useDCContext } from "@src/context/dcContext";
import PersonalAllTimeCarbonTags from "./PersonalAllTimeCarbonTags.jsx";
import PersonalAnnualCarbonTags from "./PersonalAnnualCarbonTags.jsx";
import HelpingLogoMakeADifference from "./HelpingLogoMakeADifference.jsx";
import { useIsMobileWidth, useIsTabletWidth } from "@src/utils/mediaQuery.js";
import { convertFuelBurnedToCo2 } from "@signolteam/shared/utils";
import { trackActionActivity } from "../../../utils/userActivityTracker.jsx";
import { useOperatorContext } from "@src/context/operatorContext.jsx";

/**
 * @param {{
 *   customerYearlyImpact: Array<{ totalDonations: number, totalFuelSaved: number, year: string, impactPerBvr: any[] }>,
 *   operatorPeriodImpact: Array<{ behaviourGroupCode: string, behaviourGroupId: string, donationTotal: number, goalTargetAchievedOperator: boolean, periodKey: string, sumSavingsKg: number }>,
 *   operatorYearlyImpact: Array<{ totalDonations: number, totalFuelSaved: number, year: string, impactPerBvr: any[] }>,
 * }} props
 */
const ImpactCarbonSection = ({
  customerYearlyImpact,
  operatorPeriodImpact,
  operatorYearlyImpact,
}) => {
  const { profile } = useOperatorContext();
  const { operator, customer, behaviours } = profile;
  const { industry } = customer;
  const customerCode = customer.customerCode.toLowerCase();
  const { getDynamicReplacement } = useDCContext();
  const isMobile = useIsMobileWidth();
  const isTablet = useIsTabletWidth();
  const personalAnnualYearOptions = operatorYearlyImpact
    .map((o) => o.year)
    .map(Number);
  const [personalAnnualCurrentYear, setPersonalAnnualCurrentYear] = useState(
    Math.max(...personalAnnualYearOptions).toString(),
  );

  const personalAnnualCurrentStats = operatorYearlyImpact.find(
    (o) => o.year === personalAnnualCurrentYear,
  );
  const personalAnnualSavingsKgCo2 = personalAnnualCurrentStats?.totalFuelSaved
    ? convertFuelBurnedToCo2(personalAnnualCurrentStats.totalFuelSaved)
    : undefined;
  const personalAllTimeSavingsKgCo2 = convertFuelBurnedToCo2(
    _.sumBy(operatorYearlyImpact, (o) => o.totalFuelSaved),
  );
  const companyAllTimeSavingsKgCo2 = convertFuelBurnedToCo2(
    _.sumBy(customerYearlyImpact, (o) => o.totalFuelSaved),
  );

  const nextMilestoneKgCo2 = !!operator?.nextMilestoneFuelValue
    ? Number(convertFuelBurnedToCo2(operator.nextMilestoneFuelValue))
    : personalAllTimeSavingsKgCo2
      ? personalAllTimeSavingsKgCo2 + 3160 //1000kg fuel
      : 100;

  //Calculate both equivalences at once so they avoid using the same equivalence rule
  const [equivalencePersonalAnnual, equivalencePersonalAllTime] =
    new Co2Equivalences().computeMany(industry, [
      { subject: "operator", co2Kg: personalAnnualSavingsKgCo2 },
      { subject: "operator", co2Kg: personalAllTimeSavingsKgCo2 },
    ]);

  return (
    <section className="w-full text-black text-center fade-in">
      <div
        className={`${isMobile || isTablet ? "flex flex-col justify-center items-stretch p-6" : "grid grid-cols-2 p-12"} gap-6`}
      >
        <CardContainer
          padding={6}
          footerImage={
            (getDynamicReplacement("captain__impact__carbon_savings_img_url"),
            {},
            "https://signol-email-images.s3.eu-west-2.amazonaws.com/ImpactPage_milestone.svg")
          }
          shadow={false}
        >
          <div className="relative">
            <Tooltip
              direction="down-right"
              content={[
                getDynamicReplacement(
                  "captain__tooltip__impact_carbon_savings",
                ),
              ]}
            />
          </div>
          <VerticalLayout
            title={getDynamicReplacement(
              "captain__impact__operator_annual_title",
              [],
              "Personal Annual",
              true,
            )}
          >
            <div>
              {/* Year Select */}
              <DropDown
                value={personalAnnualCurrentYear}
                options={personalAnnualYearOptions
                  .sort((a, b) => Number(b) - Number(a))
                  .map((y) => ({
                    value: y,
                    text: y.toString(),
                  }))}
                onChange={(newValue) => {
                  trackActionActivity("IMPACT_SWITCH_YEAR", {
                    year: newValue,
                    tab: "carbon",
                  });
                  setPersonalAnnualCurrentYear(newValue);
                }}
                className={isMobile ? `px-3 mx-auto mb-2` : "w-32 mx-auto"}
              />
              <HorizontalResponsiveLayout
                horizontal={{ ratio: [1, 1] }}
                vertical={{ className: "p-3" }}
                gap={4}
              >
                <PersonalAnnualCarbonTags
                  industry={industry}
                  operatorSingleYearImpact={personalAnnualCurrentStats}
                  customerBehaviours={behaviours}
                />
                <SavingsCard
                  mode="co2"
                  value={personalAnnualSavingsKgCo2}
                  industry={industry}
                  customerCode={customerCode}
                />
              </HorizontalResponsiveLayout>
            </div>
            <div>
              {equivalencePersonalAnnual && (
                <HorizontalLayout ratio={["flex-1"]}>
                  <ImageWrapper src={equivalencePersonalAnnual?.iconUrl} />
                  <p className="text-left flex-grow">
                    {equivalencePersonalAnnual?.equivalenceText}
                  </p>
                </HorizontalLayout>
              )}
              <div className="text-primary-500 text-center mt-2">
                {getDynamicReplacement(
                  "captain__impact__your_next_milestone",
                  {
                    NEXT_MILESTONE: formatMass(industry, nextMilestoneKgCo2)
                      .asString,
                  },
                  `Your next milestone is <strong>${formatMass(industry, nextMilestoneKgCo2).asString}</strong>.`,
                )}
              </div>
            </div>
          </VerticalLayout>
        </CardContainer>

        {/* SECOND COLUMN */}
        <VerticalLayout layoutType="flex-bottom" gap={4}>
          <CardContainer padding={6} shadow={false}>
            <h4 className="mb-2">
              {getDynamicReplacement(
                "captain__impact__operator_alltime_title",
                [],
                "Personal All-time",
              )}
            </h4>
            <HorizontalResponsiveLayout horizontal={{ ratio: [1, 1] }} gap={4}>
              <PersonalAllTimeCarbonTags
                industry={industry}
                operatorPeriodImpact={operatorPeriodImpact}
                customerBehaviours={behaviours}
              />
              <SavingsEquivalenceFlippableCard
                industry={industry}
                subject="operator"
                kgCo2={personalAllTimeSavingsKgCo2}
                overrideEquivalence={equivalencePersonalAllTime}
                customerCode={customerCode}
                trackingActionKey="IMPACT_FLIP_PERSONAL_ALL_TIME_SAVINGS"
              />
            </HorizontalResponsiveLayout>
          </CardContainer>

          <CardContainer padding={6} shadow={false}>
            <div className="flex flex-col gap-2">
              <h4>
                {getDynamicReplacement(
                  "captain__impact__customer_alltime_title",
                  [],
                  "Company All-time",
                )}
              </h4>
              <SavingsEquivalenceFlippableCard
                industry={industry}
                subject="global"
                kgCo2={companyAllTimeSavingsKgCo2}
                customerCode={customerCode}
                layout="horizontal"
                trackingActionKey="IMPACT_FLIP_CUSTOMER_ALL_TIME_SAVINGS"
              />

              <HelpingLogoMakeADifference customer={customer} />
            </div>
          </CardContainer>
        </VerticalLayout>
      </div>
    </section>
  );
};

export default ImpactCarbonSection;
